<script setup lang="ts">
import { useAttrs } from 'vue';
import { useFieldControlId } from './fieldContext';

defineOptions({ inheritAttrs: false });

withDefaults(
  defineProps<{
    type?: string;
    placeholder?: string;
    invalid?: boolean;
    disabled?: boolean;
  }>(),
  { type: 'text', invalid: false, disabled: false },
);

// `v-model.number` / `.trim` work on this component as on a native input — Vue applies the
// parent's modifiers in emit; pinned by backoffice-ui/src/components/libInputModifiers.spec.ts.
const model = defineModel<string | number>();
const attrs = useAttrs();
const controlId = useFieldControlId(() => attrs.id as string | undefined);
</script>

<template>
  <span class="lumi-input-wrap" style="display: block">
    <span v-if="$slots.icon" class="lumi-input-wrap__icon"><slot name="icon" /></span>
    <input
      v-model="model"
      v-bind="$attrs"
      :id="controlId"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      class="lumi-input"
      :class="{ 'lumi-input--invalid': invalid, 'lumi-input--with-icon': $slots.icon }"
    />
  </span>
</template>

<style>
/* Spec §8: white on the field border, 10 px radius, 40 px tall. Keyboard focus
   shows the espresso ring (tokens/base.css *:focus-visible); the border-colour
   shift marks the active field on any focus. */
.lumi-input{display:block;width:100%;height:var(--field-h);padding:0 12px;border:1px solid var(--field-border);border-radius:var(--radius-md);background:var(--field-bg);color:var(--text-heading);font-family:var(--font-body);font-size:var(--text-sm);line-height:1.5;transition:border-color var(--dur-fast);}
.lumi-input::placeholder{color:var(--text-tertiary);}
.lumi-input:focus{border-color:var(--panel-edge);}
.lumi-input:disabled{background:var(--surface-wash);color:var(--text-secondary);cursor:not-allowed;}
.lumi-input--invalid{border-color:var(--danger);}
.lumi-input--invalid:focus{border-color:var(--danger);}
.lumi-input--invalid:focus-visible{outline-color:var(--danger);}
.lumi-input-wrap{position:relative;}
.lumi-input-wrap .lumi-input--with-icon{padding-left:38px;}
.lumi-input-wrap__icon{position:absolute;left:12px;top:50%;transform:translateY(-50%);width:18px;height:18px;color:var(--text-tertiary);pointer-events:none;display:flex;}
.lumi-input-wrap__icon svg{width:100%;height:100%;}
</style>
